import { useState, useEffect, useCallback } from "react";
import { persistGclid } from "@/lib/googleTracking";
import { persistFbc } from "@/lib/facebookTracking";
import { CONSENT_KEY, CONSENT_VERSION, gespeicherteEinwilligung, type GespeicherteEinwilligung } from "@/lib/consentSpeicher";
import { nachAktivierung } from "@/lib/prerendering";
import { GTAG_SOFORT_PFADE, nachLeerlauf, pfadPasst } from "@/lib/trackingLadezeitpunkt";
import { zaehleErsteEntscheidung } from "@/lib/einwilligungsZaehlung";
import { isNative } from "@/lib/platform";
import { nichtErlaubteSpeicherungenEntfernen } from "@/lib/geraeteSpeicher";
import { attributionSpeichern } from "@/hooks/useUtmCapture";
import { GA4_ID, consentModeWerte, googleTagZiele } from "@/lib/googleTag";
import { GOOGLE_ADS_ID } from "@/lib/googleAnalytics";

// Key, Version und Lesen liegen in consentSpeicher (gemeinsam mit den Tracking-Helfern).
export type CookieConsentState = GespeicherteEinwilligung;

const getStoredConsent = gespeicherteEinwilligung;

/** Nicht-Hook-Zugriff für Code außerhalb von React (z. B. main.tsx). */
export function hasStoredMarketingConsent(): boolean {
  return getStoredConsent()?.marketing === true;
}

// ── Geteilter Consent-Zustand (B2, 18.09.2026) ────────────────────────────────
// Jeder useCookieConsent()-Aufruf hatte eigenen useState. Klickte jemand im
// Banner auf „Alle akzeptieren", erfuhren die anderen Instanzen (Footer,
// ClarityLoader, useMetaPageView) davon bis zum nächsten Seitenladen nichts:
// `hasMarketingConsent` blieb dort false. Ausgerechnet in der Session, in der
// zugestimmt wurde, fehlten damit SPA-PageViews und Advanced Matching — also
// genau bei den Besuchern, die gerade aus einer Anzeige kamen.
// Deshalb: ein Modul-weites Abo. localStorage bleibt die Quelle der Wahrheit,
// der Broadcast hält nur alle React-Instanzen synchron.
type ConsentListener = (c: CookieConsentState | null) => void;
const consentListeners = new Set<ConsentListener>();
function broadcastConsent(next: CookieConsentState | null) {
  consentListeners.forEach((l) => {
    try {
      l(next);
    } catch {
      /* eine kaputte Instanz darf die anderen nicht blockieren */
    }
  });
}

export function useCookieConsent() {
  const [consent, setConsentState] = useState<CookieConsentState | null>(() => getStoredConsent());
  // Phase 2f: showBanner startet IMMER false, wird erst nach LCP-Fenster (1500ms)
  // auf true geflippt wenn kein Consent vorhanden. So konkurriert das Banner
  // nicht mit dem Hero-Image um LCP-Position.
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Defer banner-render bis nach LCP (1.5s nach Mount)
  useEffect(() => {
    if (getStoredConsent() !== null) return; // Schon entschieden
    const t = setTimeout(() => {
      // Zweite Prüfung: in der Zwischenzeit kann eine andere Instanz gespeichert
      // haben (B2) — dann darf hier kein Banner mehr aufpoppen.
      if (getStoredConsent() !== null) return;
      setShowBanner(true);
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  // Auf Entscheidungen anderer Instanzen hören (B2).
  useEffect(() => {
    const listener: ConsentListener = (next) => {
      setConsentState(next);
      setShowBanner(false);
      setShowSettings(false);
    };
    consentListeners.add(listener);
    return () => {
      consentListeners.delete(listener);
    };
  }, []);

  const saveConsent = useCallback((newConsent: Omit<CookieConsentState, "necessary" | "timestamp" | "version">) => {
    // Read previous consent BEFORE overwriting
    const prev = getStoredConsent();

    const full: CookieConsentState = {
      necessary: true,
      analytics: newConsent.analytics,
      marketing: newConsent.marketing,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(full));
    // Alle Instanzen (inkl. dieser) auf denselben Stand bringen — s. B2 oben.
    broadcastConsent(full);

    // Speicherungen auf dem Gerät an die Entscheidung anpassen (Datenstrategie D0, 27.10.2026):
    // Nicht (mehr) Erlaubtes entfernen; Erlaubtes aus dieser Sitzung nachtragen, damit Herkunft,
    // Besucher- und Sitzungskennung ab jetzt zusammenhängen. Die beiden Messmodule liegen in
    // eigenen Chunks und sind zu diesem Zeitpunkt ohnehin geladen (Web-Vitals, Website-Messung).
    nichtErlaubteSpeicherungenEntfernen(full);
    if (full.analytics || full.marketing) attributionSpeichern();
    if (full.analytics) {
      import("@/lib/experiments").then((m) => m.besucherKennungSpeichern()).catch(() => {});
      import("@/lib/interactions").then((m) => m.sitzungSpeichern()).catch(() => {});
    }

    // Anonyme Einwilligungsquote (nur erste Entscheidung, nur Tageszähler, KA-22).
    zaehleErsteEntscheidung(prev !== null, full.analytics, full.marketing, isNative());

    // Marketing-Opt-in in derselben Session: jetzt darf der beim Landen
    // gestashte gclid als Cookie persistiert werden (Offline-Conversions).
    if (full.marketing) {
      persistGclid();
      persistFbc();
    }

    // Update Google Consent Mode v2
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", consentModeWerte(full));
    }

    // If previously loaded scripts are now denied, reload to remove them
    if (prev) {
      const needsReload =
        (prev.analytics && !full.analytics) || (prev.marketing && !full.marketing);
      if (needsReload) {
        window.location.reload();
        return;
      }
    }

    // Load scripts if newly consented. gtag.js auch bei „nur Marketing“, dann nur für
    // Google Ads (Noah 27.10.2026, s. src/lib/googleTag.ts).
    if (full.analytics || full.marketing) loadGoogleTag();
    if (full.marketing) loadMetaPixel();
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ analytics: true, marketing: true });
  }, [saveConsent]);

  const acceptNecessaryOnly = useCallback(() => {
    saveConsent({ analytics: false, marketing: false });
  }, [saveConsent]);

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // On mount: GA4 nur mit Statistik-Einwilligung laden (Consent Mode „einfach“,
  // Noah 27.10.2026, Datenstrategie D0b). Bis dahin lud gtag.js bei jedem Besuch und
  // sendete ohne Einwilligung cookielose Pings („erweitert“); das ist ein Zugriff im
  // Sinne von § 25 TDDDG. Ohne Einwilligung bleibt gtag.js jetzt ungeladen, Befehle
  // (page_view, Events) landen nur im dataLayer im Speicher und gehen nirgends hin.
  // Besuche und Kanäle zählt die eigene Messung (web_sessions) weiter vollständig.
  // Nachtrag Noah 27.10.2026: gtag.js lädt auch bei reiner Marketing-Einwilligung,
  // dann aber nur für Google Ads (Conversions): kein GA4-`config`, analytics_storage
  // bleibt denied, keine page_views an GA4. Google Ads hängt nur noch an Marketing,
  // GA4 nur an Statistik (src/lib/googleTag.ts).
  // Meta Pixel bleibt wie bisher an die Marketing-Einwilligung gebunden.
  useEffect(() => {
    if (consent?.analytics || consent?.marketing) loadGoogleTag();
    if (consent?.marketing) loadMetaPixel();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    consent,
    showBanner,
    showSettings,
    acceptAll,
    acceptNecessaryOnly,
    saveConsent,
    openSettings,
    closeSettings,
    hasAnalyticsConsent: consent?.analytics ?? false,
    hasMarketingConsent: consent?.marketing ?? false,
  };
}

// --- Script loaders ---

// Bot-Detection: blocks obvious crawlers/headless-browsers from polluting GA4.
// Pattern covers: standard search-engine bots, headless browsers used by
// scrapers, common monitoring tools. Conservative pattern — does NOT match
// real users on VPN/cloud (user-agent says Chrome/Firefox normally).
const BOT_UA_PATTERN = /bot|crawl|spider|slurp|headless|phantom|selenium|puppeteer|playwright|lighthouse|wget|curl|python-requests|go-http-client|java\/|axios|insomnia|postman|httpclient/i;

function isLikelyBot(): boolean {
  if (typeof navigator === "undefined") return false;
  return BOT_UA_PATTERN.test(navigator.userAgent || "");
}

// Production-Host-Guard: Lovable-Preview- und Staging-Hosts
// (charliescrew.lovable.app, *.lovableproject.com, id-preview--*.lovable.app)
// feuern sonst in dieselbe GA4-Property und dasselbe Ads-Konto wie echter
// Traffic. Das verzerrt Sessions, Conversion-Rate und damit CAC pro Kanal.
// Apex + alle eigenen Subdomains sind erlaubt, damit spätere Subdomains
// (app., buchung., …) nicht still ungemessen bleiben.
function isProductionHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "charliescrew.com" || host.endsWith(".charliescrew.com");
}

// gtag.js wird höchstens einmal eingehängt; GA4 und Google Ads werden getrennt und
// jeweils höchstens einmal konfiguriert. Wer zuerst nur Marketing erlaubt und später
// Statistik dazunimmt, bekommt GA4 ohne Neuladen dazu (und umgekehrt).
let gtagGeprueft = false;
let gtagErlaubt = false;
let ga4Konfiguriert = false;
let adsKonfiguriert = false;

/**
 * Lädt gtag.js, sobald Statistik ODER Marketing erlaubt ist (Noah 27.10.2026), und
 * konfiguriert nur die Ziele, die die gespeicherte Einwilligung freigibt
 * (src/lib/googleTag.ts): GA4 nur mit Statistik, Google Ads nur mit Marketing.
 * Bei „nur Marketing“ gibt es also kein GA4-`config`, analytics_storage bleibt denied
 * und useGoogleAnalytics schickt keine page_views.
 */
export function loadGoogleTag() {
  const ziele = googleTagZiele(getStoredConsent());
  if (!ziele.laden) return;

  if (!gtagGeprueft) {
    gtagGeprueft = true;
    // Skip gtag entirely for obvious bots. Saves quota + cleans reports.
    if (isLikelyBot()) {
      console.debug("[gtag] skipped (bot UA detected)");
      return;
    }
    if (!isProductionHost()) {
      console.debug("[gtag] skipped (non-production host:", window.location.hostname, ")");
      return;
    }
    gtagErlaubt = true;
    // Bei „nur Marketing“ das Skript über die Ads-ID laden, damit nicht einmal der
    // GA4-Container mitkommt. Weitere Ziele lädt gtag.js beim `config` selbst nach.
    gtagEinhaengen(ziele.ga4 ? GA4_ID : GOOGLE_ADS_ID);
  }
  if (!gtagErlaubt) return;

  const gtag = window.gtag;
  // Consent-Stand bei jedem Aufruf spiegeln (auch beim Nachrüsten eines Ziels).
  gtag("consent", "update", consentModeWerte(getStoredConsent()));

  if (ziele.ga4 && !ga4Konfiguriert) {
    ga4Konfiguriert = true;
    // send_page_view:false — page_views kommen ausschließlich aus useGoogleAnalytics,
    // damit der Einstieg nicht doppelt zählt.
    gtag("config", GA4_ID, { send_page_view: false });
    // Statistik kam erst nach dem Laden der Seite dazu: den page_view, den
    // useGoogleAnalytics mangels Einwilligung ausgelassen hat, einmal nachholen.
    if (ga4PageViewAusgelassen) {
      ga4PageViewAusgelassen = false;
      gtag("event", "page_view", {
        send_to: GA4_ID,
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }
  if (ziele.ads && !adsKonfiguriert) {
    adsKonfiguriert = true;
    gtag("config", GOOGLE_ADS_ID);
  }
}

let ga4PageViewAusgelassen = false;
/** useGoogleAnalytics meldet hier, dass es einen page_view mangels Statistik-Einwilligung nicht gesendet hat. */
export function merkeAusgelassenenGa4PageView() {
  ga4PageViewAusgelassen = true;
}

function gtagEinhaengen(skriptId: string) {
  // gtag.js (193 KB) erst nach dem ersten Bild (Perf-Audit 18.09.2026, Punkt 3).
  // Nur das Skript-Tag wartet; alle Befehle unten laufen weiter sofort in den
  // dataLayer und werden beim Laden in Originalreihenfolge nachgeholt (Stub aus
  // index.html). Auch der erste page_view aus useGoogleAnalytics landet so im
  // Puffer, nicht im Nichts. Auf Conversion-/Erfolgsseiten und im eingeloggten
  // Bereich (GTAG_SOFORT_PFADE) lädt das Skript weiter sofort, sonst ginge die
  // Ads-Conversion verloren, wenn jemand den Tab schnell schließt.
  // Vorab gerenderte Tabs (Speculation Rules): in beiden Fällen erst nach der
  // Aktivierung, wie jedes Analyse-Skript (Regel in prerendering.ts).
  const injectScript = () => {
    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(skriptId)}`;
    script.async = true;
    document.head.appendChild(script);
  };
  nachAktivierung(() => {
    if (pfadPasst(window.location.pathname, GTAG_SOFORT_PFADE)) injectScript();
    else nachLeerlauf(injectScript);
  });

  window.dataLayer = window.dataLayer || [];

  // WICHTIG: gtag.js erkennt einen Command NUR, wenn ein `arguments`-Objekt in
  // den dataLayer gepusht wird. Ein echtes Array (`...args`) wird still
  // verworfen — kein Fehler, kein Log. Genau das hat hier monatelang jeden
  // Command aus loadGA4() unwirksam gemacht: `js`, `consent update` und beide
  // `config`. Ohne `js` sendet GA4 überhaupt keinen Hit, weshalb GA4 nur ~2 %
  // des echten Traffics sah und der Ads-Container AW-17560289148 nie
  // initialisiert wurde. index.html definiert window.gtag bereits korrekt —
  // hier deshalb NICHT überschreiben, nur absichern, falls es fehlt.
  if (typeof window.gtag !== "function") {
    window.gtag = function gtagShim() {
      // eslint-disable-next-line prefer-rest-params
      (window.dataLayer as unknown[]).push(arguments);
    } as unknown as (...args: unknown[]) => void;
  }

  window.gtag("js", new Date());
  // Die `config`-Befehle für GA4 und Google Ads setzt loadGoogleTag() je nach Einwilligung.
  // index.html konfiguriert seit 27.10.2026 nichts mehr vorab, sonst würde GA4 auch bei
  // reiner Marketing-Einwilligung mitlaufen.
}

let metaPixelLoaded = false;

// ── Doppelter PageView (B1, 18.09.2026) ──────────────────────────────────────
// loadMetaPixel feuert direkt nach `init` einen PageView; useMetaPageView feuerte
// im selben Commit einen zweiten. Meta zählte damit jeden Einstieg doppelt — der
// Nenner jeder Conversion-Rate war zu groß. Der PageView bleibt hier (direkt nach
// init ist er garantiert, useMetaPageView kann zu früh laufen, solange window.fbq
// noch fehlt); useMetaPageView „verbraucht" ihn stattdessen einmal.
let metaPixelPageViewPending = false;
/**
 * true, wenn loadMetaPixel gerade einen PageView gefeuert hat, den useMetaPageView
 * nicht wiederholen soll. Gibt den Merker beim Lesen frei (einmalig).
 */
export function consumeMetaPixelPageView(): boolean {
  if (!metaPixelPageViewPending) return false;
  metaPixelPageViewPending = false;
  return true;
}

export function loadMetaPixel() {
  if (metaPixelLoaded) return;
  metaPixelLoaded = true;

  // Skip Meta Pixel for obvious bots — same pattern as GA4.
  if (isLikelyBot()) {
    console.debug("[MetaPixel] skipped (bot UA detected)");
    return;
  }

  if (!isProductionHost()) {
    console.debug("[MetaPixel] skipped (non-production host:", window.location.hostname, ")");
    return;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const w = window as any;
  const n: any = w.fbq = function() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
  if (!w._fbq) w._fbq = n;
  n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
  /* eslint-enable @typescript-eslint/no-explicit-any */
  window.fbq("init", "850802317703311");
  // Vorab gerenderte Seiten (Chrome lädt Treffer aus der Suche teils vorab, Feld:
  // navigation_type = prerender): fbevents.js kennt document.prerendering nicht und
  // würde den PageView zählen, bevor jemand die Seite sieht. Deshalb erst nach der
  // Aktivierung; useMetaPageView liest den Merker ebenfalls erst dann.
  nachAktivierung(() => {
    window.fbq("track", "PageView");
    metaPixelPageViewPending = true; // s. consumeMetaPixelPageView (B1)
  });

  // Die fbq-Warteschlange steht sofort (Events wie CompleteRegistration/Purchase gehen nicht
  // verloren, sie werden beim Laden des Skripts abgearbeitet). Nur das ~100-KB-Skript selbst
  // lädt im eingeloggten Bereich erst nach Leerlauf, damit es die Dashboard-Abfragen nicht
  // ausbremst (Perf Welle 5). Marketing-Seiten: unverändert sofort.
  const injectScript = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  };
  if (isEingeloggterBereich()) {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(injectScript, { timeout: 3000 });
    else setTimeout(injectScript, 3000);
  } else {
    injectScript();
  }
}

/** Eingeloggte App-Bereiche (Kunden-Dashboard, Hundeprofil, Admin, Walker, Export). */
const EINGELOGGT_PFADE = ["/dashboard", "/dog/", "/admin", "/walker", "/export"];
function isEingeloggterBereich(): boolean {
  const path = window.location.pathname;
  return EINGELOGGT_PFADE.some((p) => (p.endsWith("/") ? path.startsWith(p) : path === p || path.startsWith(p + "/")));
}

// Declare dataLayer on window
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
